import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  MessageCircle, Ticket, Settings, BarChart3, Send, Loader2,
  CheckCircle2, Clock, AlertCircle, XCircle, Users, TrendingUp,
  Bell, Volume2, Search, Filter, RefreshCw, Eye, Maximize2, Minimize2, X
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSupport() {
  const [activeTab, setActiveTab] = useState('chats');
  const [conversations, setConversations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [reports, setReports] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Chat state
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Ticket state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [ticketReply, setTicketReply] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  
  // Minimized chats (like FB Messenger)
  const [minimizedChats, setMinimizedChats] = useState([]);
  const [openChats, setOpenChats] = useState([]);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    try {
      const [convsRes, ticketsRes, reportsRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/chat/conversations?is_admin=true`),
        fetch(`${API_URL}/api/tickets?is_admin=true`),
        fetch(`${API_URL}/api/support/reports`),
        fetch(`${API_URL}/api/support/config`)
      ]);
      
      if (convsRes.ok) {
        const data = await convsRes.json();
        setConversations(data.conversations || []);
      }
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setTickets(data.tickets || []);
      }
      if (reportsRes.ok) {
        setReports(await reportsRes.json());
      }
      if (configRes.ok) {
        setConfig(await configRes.json());
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/messages/${conversationId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        // Mark as read
        await fetch(`${API_URL}/api/chat/mark-read/${conversationId}?reader_type=admin`, { method: 'POST' });
        loadData();
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = async (conversationId) => {
    if (!newMessage.trim()) return;
    
    setIsSending(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/send?conversation_id=${conversationId}&sender_id=admin&sender_name=Admin&sender_type=admin&message=${encodeURIComponent(newMessage)}`, {
        method: 'POST'
      });
      if (res.ok) {
        setNewMessage('');
        loadMessages(conversationId);
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const loadTicketDetails = async (ticketId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket({ ...data.ticket, responses: data.responses });
      }
    } catch (error) {
      console.error('Failed to load ticket:', error);
    }
  };

  const sendTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/respond?responder_id=admin&responder_name=Admin&responder_type=admin&message=${encodeURIComponent(ticketReply)}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Reply sent');
        setTicketReply('');
        loadTicketDetails(selectedTicket.id);
        loadData();
      }
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}/status?status=${status}&admin_id=admin&admin_name=Admin${status === 'resolved' && resolutionNotes ? `&resolution_notes=${encodeURIComponent(resolutionNotes)}` : ''}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success(`Ticket ${status}`);
        setResolutionNotes('');
        loadTicketDetails(ticketId);
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const saveConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/support/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        toast.success('Configuration saved');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const openChatWindow = (conv) => {
    if (!openChats.find(c => c.id === conv.id)) {
      setOpenChats([...openChats.slice(-2), conv]); // Max 3 open chats
      loadMessages(conv.id);
    }
    setMinimizedChats(minimizedChats.filter(c => c.id !== conv.id));
  };

  const minimizeChat = (conv) => {
    setOpenChats(openChats.filter(c => c.id !== conv.id));
    if (!minimizedChats.find(c => c.id === conv.id)) {
      setMinimizedChats([...minimizedChats, conv]);
    }
  };

  const closeChat = (conv) => {
    setOpenChats(openChats.filter(c => c.id !== conv.id));
    setMinimizedChats(minimizedChats.filter(c => c.id !== conv.id));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'resolved': return 'bg-green-500';
      case 'closed': return 'bg-muted/500';
      default: return 'bg-muted/500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'resolved': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'closed': return <XCircle className="h-4 w-4 text-muted-foreground" />;
      default: return null;
    }
  };

  const filteredTickets = tickets.filter(t => 
    ticketFilter === 'all' || t.status === ticketFilter
  );

  const CHART_COLORS = ['#3b82f6', '#22c55e', '#eab308', '#64748b'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <span className="text-4xl">🤖</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Support Center</h1>
            <p className="text-blue-100 mt-1">
              Manage chats, tickets, and user support
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Chats</p>
                <p className="text-2xl font-bold">{reports?.chat?.active_conversations || 0}</p>
              </div>
              <MessageCircle className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Tickets</p>
                <p className="text-2xl font-bold">{reports?.tickets?.open || 0}</p>
              </div>
              <Ticket className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolution Rate</p>
                <p className="text-2xl font-bold">{reports?.tickets?.resolution_rate || 0}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">SLA Breached</p>
                <p className="text-2xl font-bold text-red-500">{reports?.tickets?.sla_breached || 0}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="chats" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Chats
            {conversations.reduce((sum, c) => sum + (c.unread_count_admin || 0), 0) > 0 && (
              <Badge variant="destructive" className="h-5 px-1.5">
                {conversations.reduce((sum, c) => sum + (c.unread_count_admin || 0), 0)}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tickets" className="flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            Tickets
            {tickets.filter(t => t.status === 'open').length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5">
                {tickets.filter(t => t.status === 'open').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Chats Tab */}
        <TabsContent value="chats">
          <Card>
            <CardHeader>
              <CardTitle>User Conversations</CardTitle>
              <CardDescription>Click on a conversation to open chat window</CardDescription>
            </CardHeader>
            <CardContent>
              {conversations.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No conversations yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Last Message</TableHead>
                      <TableHead>Unread</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conversations.map((conv) => (
                      <TableRow key={conv.id} className="cursor-pointer hover:bg-muted/50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{conv.user_name}</p>
                            <p className="text-xs text-muted-foreground">{conv.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                          {conv.last_message || 'No messages'}
                        </TableCell>
                        <TableCell>
                          {conv.unread_count_admin > 0 && (
                            <Badge variant="destructive">{conv.unread_count_admin}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" onClick={() => openChatWindow(conv)}>
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Open Chat
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Support Tickets</CardTitle>
                  <CardDescription>Manage and resolve user issues</CardDescription>
                </div>
                <Select value={ticketFilter} onValueChange={setTicketFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tickets</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {filteredTickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tickets found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>SLA</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTickets.map((ticket) => {
                      const slaBreached = ticket.sla_deadline && new Date(ticket.sla_deadline) < new Date() && ['open', 'in_progress'].includes(ticket.status);
                      return (
                        <TableRow key={ticket.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(ticket.status)}
                              <span className="capitalize text-sm">{ticket.status.replace('_', ' ')}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium max-w-[200px] truncate">
                            {ticket.subject}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm">{ticket.user_name}</p>
                              <p className="text-xs text-muted-foreground">{ticket.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={ticket.priority === 'high' || ticket.priority === 'critical' ? 'destructive' : 'secondary'}>
                              {ticket.priority}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {slaBreached ? (
                              <Badge variant="destructive">Breached</Badge>
                            ) : ticket.sla_deadline ? (
                              <span className="text-xs text-muted-foreground">
                                {new Date(ticket.sla_deadline).toLocaleString()}
                              </span>
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => loadTicketDetails(ticket.id)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ticket Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Ticket Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Open', value: reports?.tickets?.open || 0 },
                        { name: 'In Progress', value: reports?.tickets?.in_progress || 0 },
                        { name: 'Resolved', value: reports?.tickets?.resolved || 0 },
                        { name: 'Closed', value: reports?.tickets?.closed || 0 }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {CHART_COLORS.map((color, index) => (
                        <Cell key={index} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Average Resolution Time</p>
                    <p className="text-2xl font-bold">{reports?.performance?.avg_resolution_hours || 0}h</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">SLA Compliance Rate</p>
                    <p className="text-2xl font-bold">{reports?.performance?.sla_compliance_rate || 100}%</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Messages</p>
                    <p className="text-2xl font-bold">{reports?.chat?.total_messages || 0}</p>
                  </div>
                  <MessageCircle className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Support Configuration</CardTitle>
              <CardDescription>Configure SLA and notification settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default SLA Hours</Label>
                  <Input
                    type="number"
                    value={config?.sla_hours || 24}
                    onChange={(e) => setConfig({ ...config, sla_hours: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground">Time to respond to tickets</p>
                </div>
                <div className="space-y-2">
                  <Label>Auto-close Resolved (Days)</Label>
                  <Input
                    type="number"
                    value={config?.auto_close_resolved_days || 7}
                    onChange={(e) => setConfig({ ...config, auto_close_resolved_days: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground">Days after which resolved tickets auto-close</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-medium">Priority Levels</h4>
                {config?.priority_levels?.map((level, idx) => (
                  <div key={level.value} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                    <Badge className="capitalize">{level.label}</Badge>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">SLA Hours:</Label>
                      <Input
                        type="number"
                        value={level.sla_hours}
                        onChange={(e) => {
                          const updated = [...config.priority_levels];
                          updated[idx].sla_hours = parseInt(e.target.value);
                          setConfig({ ...config, priority_levels: updated });
                        }}
                        className="w-20"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={config?.enable_sound_notifications}
                    onCheckedChange={(v) => setConfig({ ...config, enable_sound_notifications: v })}
                  />
                  <Label>Sound Notifications</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={config?.enable_visual_notifications}
                    onCheckedChange={(v) => setConfig({ ...config, enable_visual_notifications: v })}
                  />
                  <Label>Visual Notifications</Label>
                </div>
              </div>

              <Button onClick={saveConfig}>Save Configuration</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getStatusIcon(selectedTicket?.status)}
              {selectedTicket?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Ticket Info */}
              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg mb-4">
                <Badge variant={selectedTicket.priority === 'high' ? 'destructive' : 'secondary'}>
                  {selectedTicket.priority}
                </Badge>
                <span className="text-sm text-muted-foreground">by {selectedTicket.user_name}</span>
                <span className="text-sm text-muted-foreground">
                  {new Date(selectedTicket.created_at).toLocaleString()}
                </span>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 mb-4">
                <div className="space-y-3">
                  {/* Original ticket */}
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Original Issue</p>
                    <p className="text-sm">{selectedTicket.description}</p>
                    {selectedTicket.screenshot_url && (
                      <img 
                        src={`${API_URL}${selectedTicket.screenshot_url}`} 
                        alt="Screenshot" 
                        className="mt-2 rounded border max-h-48 object-contain"
                      />
                    )}
                  </div>
                  
                  {/* Responses */}
                  {selectedTicket.responses?.map((resp) => (
                    <div
                      key={resp.id}
                      className={`p-3 rounded-lg ${
                        resp.responder_type === 'admin' ? 'bg-green-50 ml-4' : 'bg-muted/50 mr-4'
                      }`}
                    >
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        {resp.responder_type === 'admin' ? '👤 Admin' : `${selectedTicket.user_name}`}
                      </p>
                      <p className="text-sm">{resp.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(resp.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Actions */}
              {['open', 'in_progress'].includes(selectedTicket.status) && (
                <div className="space-y-3 pt-3 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a reply..."
                      value={ticketReply}
                      onChange={(e) => setTicketReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && ticketReply.trim()) {
                          e.preventDefault();
                          sendTicketReply();
                        }
                      }}
                    />
                    <Button onClick={sendTicketReply} disabled={!ticketReply.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    {selectedTicket.status === 'open' && (
                      <Button variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')}>
                        Mark In Progress
                      </Button>
                    )}
                    <div className="flex-1 flex gap-2">
                      <Input
                        placeholder="Resolution notes (optional)"
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                      />
                      <Button variant="default" onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Resolve
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating Chat Windows (FB Messenger Style) */}
      <div className="fixed bottom-4 right-4 flex items-end gap-2 z-50">
        {/* Minimized chats */}
        {minimizedChats.map((conv) => (
          <div
            key={conv.id}
            className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-blue-600 transition-colors relative"
            onClick={() => openChatWindow(conv)}
          >
            <span className="text-white font-bold">{conv.user_name?.charAt(0) || 'U'}</span>
            {conv.unread_count_admin > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {conv.unread_count_admin}
              </div>
            )}
          </div>
        ))}

        {/* Open chat windows */}
        {openChats.map((conv, idx) => (
          <div
            key={conv.id}
            className="w-80 h-96 bg-white rounded-t-lg shadow-2xl border overflow-hidden flex flex-col"
            style={{ marginRight: idx > 0 ? '8px' : '0' }}
          >
            {/* Header */}
            <div className="bg-blue-500 p-2 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  {conv.user_name?.charAt(0) || 'U'}
                </div>
                <span className="font-medium text-sm">{conv.user_name}</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={() => minimizeChat(conv)}>
                  <Minimize2 className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={() => closeChat(conv)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
            
            {/* Messages */}
            <ScrollArea className="flex-1 p-2">
              {messages.filter(m => m.conversation_id === conv.id).length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No messages yet
                </div>
              ) : (
                <div className="space-y-2">
                  {messages.filter(m => m.conversation_id === conv.id).map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                          msg.sender_type === 'admin'
                            ? 'bg-blue-500 text-white'
                            : 'bg-muted text-slate-800'
                        }`}
                      >
                        <p>{msg.message}</p>
                        <p className={`text-xs mt-1 ${msg.sender_type === 'admin' ? 'text-blue-100' : 'text-muted-foreground'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
            
            {/* Input */}
            <div className="p-2 border-t">
              <div className="flex gap-1">
                <Input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                      e.preventDefault();
                      sendMessage(conv.id);
                    }
                  }}
                  className="text-sm"
                />
                <Button size="sm" onClick={() => sendMessage(conv.id)} disabled={isSending || !newMessage.trim()}>
                  {isSending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
